"use client";

import { ReactNode, useRef, useEffect, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { twMerge } from "tailwind-merge";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  /** Optional badges or metadata rendered beside the title (e.g. admin review modal). */
  titleAside?: ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "full";
  closeOnClickOutside?: boolean;
  showCloseIcon?: boolean;
  className?: string;
  contentClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  footer?: ReactNode;
  renderAction?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  isSubmitting?: boolean;
  hideFooter?: boolean;
}

const panelTransition = {
  duration: 0.22,
  ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
};

const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  titleAside,
  maxWidth = "md",
  closeOnClickOutside = true,
  showCloseIcon = true,
  className,
  contentClassName,
  bodyClassName,
  footerClassName,
  footer,
  renderAction = false,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isSubmitting = false,
  hideFooter = false,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const reduceMotion = useReducedMotion();
  const portalTarget = useSyncExternalStore(
    () => () => {},
    () => document.body,
    () => null,
  );

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const previous = document.activeElement as HTMLElement | null;
    const id = window.requestAnimationFrame(() => {
      if (showCloseIcon && closeButtonRef.current) {
        closeButtonRef.current.focus();
      } else {
        modalRef.current?.focus();
      }
    });
    return () => {
      window.cancelAnimationFrame(id);
      if (
        previous &&
        typeof previous.focus === "function" &&
        document.contains(previous)
      ) {
        previous.focus();
      }
    };
  }, [isOpen, showCloseIcon]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onCloseRef.current();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen]);

  const handleExitComplete = () => {
    document.body.style.overflow = "";
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      closeOnClickOutside &&
      modalRef.current &&
      !modalRef.current.contains(e.target as Node)
    ) {
      onClose();
    }
  };

  const hasFooter =
    !hideFooter && (Boolean(footer) || Boolean(renderAction && onConfirm));

  const maxWidthClasses = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    "2xl": "sm:max-w-2xl",
    "3xl": "sm:max-w-3xl",
    "4xl": "sm:max-w-4xl",
    "5xl": "sm:max-w-5xl",
    full: "sm:max-w-full",
  };

  const instant = reduceMotion ? { duration: 0.01 } : undefined;

  const rootTransition = instant ?? {
    duration: 0.2,
    ease: "easeOut" as const,
  };

  const modalTree = (
    <AnimatePresence mode="sync" onExitComplete={handleExitComplete}>
      {isOpen ? (
        <motion.div
          key="sio-modal"
          className="fixed inset-0 z-[10050] overflow-y-auto"
          aria-labelledby={title ? "modal-title" : undefined}
          role="dialog"
          aria-modal="true"
          onClick={handleBackdropClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={rootTransition}
        >
          <div className="flex items-center justify-center min-h-screen p-0 text-center sm:p-0 ml-4 mr-4">
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <motion.div
              ref={modalRef}
              tabIndex={showCloseIcon ? undefined : -1}
              className={twMerge(
                "relative bg-surface rounded-sio-lg text-left shadow-sio-lg sm:my-8 sm:align-middle w-full sm:p-6 p-4",
                hasFooter
                  ? "flex max-h-[calc(100dvh-2rem)] flex-col sm:max-h-[calc(100vh-2rem)]"
                  : "inline-block align-bottom",
                showCloseIcon ? "" : "outline-none",
                maxWidthClasses[maxWidth],
                className,
              )}
              initial={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, scale: 0.96, y: 12 }
              }
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, scale: 0.96, y: 12 }
              }
              transition={instant ?? panelTransition}
            >
              {showCloseIcon && (
                <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
                  <button
                    ref={closeButtonRef}
                    type="button"
                    className="text-text-light hover:text-text-light focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              )}

              {title && (
                <div
                  className={twMerge(
                    "mb-4 flex flex-wrap items-start justify-between gap-3 pr-8",
                    hasFooter ? "shrink-0" : "",
                    contentClassName,
                  )}
                >
                  <h3
                    className="min-w-0 flex-1 text-lg font-medium leading-6 text-foreground"
                    id="modal-title"
                  >
                    {title}
                  </h3>
                  {titleAside ? (
                    <div className="flex flex-wrap items-center gap-2">
                      {titleAside}
                    </div>
                  ) : null}
                </div>
              )}

              <div
                className={twMerge(
                  hasFooter
                    ? "min-h-0 flex-1 overflow-y-auto"
                    : "max-h-[calc(100vh-200px)] overflow-y-auto",
                  bodyClassName,
                )}
              >
                {children}
              </div>

              {!hideFooter && (renderAction || footer) && (
                <div
                  className={twMerge(
                    "mt-5 sm:mt-6",
                    hasFooter ? "shrink-0" : "",
                    footerClassName,
                  )}
                >
                  {footer ? (
                    footer
                  ) : renderAction && onConfirm ? (
                    <div className="sm:flex sm:flex-row-reverse gap-3">
                      <button
                        type="button"
                        className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-[var(--button-primary-shadow)] px-4 py-2 bg-[var(--button-primary-bg)] text-base font-medium text-white hover:bg-[var(--button-primary-hover)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--button-primary-focus)] sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onConfirm}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Processing..." : confirmText}
                      </button>
                      <button
                        type="button"
                        className="mt-3 w-full inline-flex justify-center rounded-xl border border-[var(--color-border-strong)] shadow-sm px-4 py-2 bg-[var(--color-surface)] text-base font-medium text-[var(--color-foreground)] hover:bg-[var(--color-secondary)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--button-primary-focus)] sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={onCancel || onClose}
                        disabled={isSubmitting}
                      >
                        {cancelText}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  if (!portalTarget) {
    return null;
  }

  return createPortal(modalTree, portalTarget);
};

export default Modal;
